/*
 * Live monitor — workspace-scoped real-time tail. Powered by the
 * shared `useLiveEvents` hook so the same code drives the inline
 * `LivePanel` snippets on per-domain pages and the cross-tenant
 * `/admin/live` view (which only differs in `scope: "platform"`).
 *
 * v1.9.0 — At full ingest rate (47K events / few minutes) the streaming
 * row table scrolled past faster than an operator could read. The
 * primary view is now a counter grid: each category card shows
 * 1m/5m/1h counts; clicking a card opens a drawer with the latest 50
 * events for that category for drill-down.
 */

import { useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import {
  useLiveEvents,
  LIVE_CATEGORIES,
  LiveEventCategory,
  LiveEvent,
  summarizeLiveDetail,
  fmtLiveTime
} from "../lib/useLiveEvents.js";

interface LiveMonitorProps {
  /** Set on the platform-admin route to subscribe cross-tenant. */
  scope?: "platform";
}

export default function LiveMonitor({ scope }: LiveMonitorProps = {}) {
  const [paused, setPaused] = useState(false);
  const [drilledCategory, setDrilledCategory] = useState<LiveEventCategory | "all" | null>(null);
  const live = useLiveEvents({ scope, paused, tailMax: 500 });
  const counters1m = live.countersIn(60_000);
  const counters5m = live.countersIn(5 * 60_000);
  const counters1h = live.countersIn(60 * 60_000);

  const drilledEvents = useMemo<LiveEvent[]>(() => {
    if (drilledCategory == null) return [];
    if (drilledCategory === "all") return live.events.slice(0, 50);
    return live.events.filter((e) => e.category === drilledCategory).slice(0, 50);
  }, [drilledCategory, live.events]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{scope === "platform" ? "Live monitor — all workspaces" : "Live monitor"}</h1>
          <p>
            {scope === "platform"
              ? "Real-time stream of every event across every workspace on the platform. Use this to watch ingestion + sync activity in aggregate."
              : "Real-time stream of every metadata sync, data ingest, tally update, aggregate rollup, indicator evaluation, and job transition that lands in this workspace."}
          </p>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Click any counter to drill into recent events for that category.
          </p>
        </div>
        <div className="actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={`pill ${live.connectionState === "open" ? "pill-ok" : live.connectionState === "connecting" ? "pill-info" : "pill-err"}`}>
            {live.connectionState === "open" ? "● Live" : live.connectionState === "connecting" ? "Connecting…" : "Disconnected"}
          </span>
          <button className="btn" type="button" onClick={() => setPaused((p) => !p)}>
            {paused ? "Resume" : "Pause"}
          </button>
          <button className="btn btn-secondary" type="button" onClick={live.clear}>
            Clear
          </button>
        </div>
      </header>

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div
            className="counter-grid"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}
          >
            <CounterCard
              label="All events"
              color="#0B2545"
              counts={{ m1: counters1m.total, m5: counters5m.total, h1: counters1h.total }}
              onClick={() => setDrilledCategory("all")}
            />
            {LIVE_CATEGORIES.map((cat) => (
              <CounterCard
                key={cat.key}
                label={cat.label}
                color={cat.color}
                counts={{
                  m1: counters1m.byCategory[cat.key],
                  m5: counters5m.byCategory[cat.key],
                  h1: counters1h.byCategory[cat.key]
                }}
                onClick={() => setDrilledCategory(cat.key)}
              />
            ))}
          </div>
        </div>
      </section>

      {drilledCategory != null ? (
        <DrilldownDrawer
          category={drilledCategory}
          events={drilledEvents}
          scope={scope}
          onClose={() => setDrilledCategory(null)}
        />
      ) : null}
    </div>
  );
}

interface CounterCardProps {
  label: string;
  color: string;
  counts: { m1: number; m5: number; h1: number };
  onClick: () => void;
}

function CounterCard({ label, color, counts, onClick }: CounterCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card"
      style={{
        textAlign: "left",
        cursor: "pointer",
        padding: 0,
        border: "1px solid var(--border)",
        borderTop: `3px solid ${color}`,
        background: "var(--surface)"
      }}
    >
      <div className="card-body" style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color, fontWeight: 600, letterSpacing: 0.3 }}>{label}</span>
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{counts.m1}</div>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>last 1m</div>
        <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11 }}>
          <span><strong>{counts.m5}</strong> <span className="muted">5m</span></span>
          <span><strong>{counts.h1}</strong> <span className="muted">1h</span></span>
        </div>
      </div>
    </button>
  );
}

interface DrilldownDrawerProps {
  category: LiveEventCategory | "all";
  events: LiveEvent[];
  scope?: "platform";
  onClose: () => void;
}

function DrilldownDrawer({ category, events, scope, onClose }: DrilldownDrawerProps) {
  const cat = category === "all" ? null : LIVE_CATEGORIES.find((c) => c.key === category) ?? null;
  const title = category === "all" ? "All events" : (cat?.label ?? String(category));
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(11,37,69,0.45)",
        zIndex: 50, display: "flex", justifyContent: "flex-end"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(960px, 100%)", height: "100%", background: "var(--surface)",
          boxShadow: "-12px 0 32px rgba(11,37,69,0.18)", display: "flex", flexDirection: "column"
        }}
      >
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="pill" style={{ background: cat?.color ?? "#0B2545", color: "#fff" }}>{title}</span>
            <span className="muted" style={{ fontSize: 12 }}>last {events.length} event{events.length === 1 ? "" : "s"}</span>
          </div>
          <button className="btn btn-secondary" type="button" onClick={onClose}>Close</button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: 0 }}>
          {events.length === 0 ? (
            <div className="empty" style={{ padding: 32, textAlign: "center" }}>
              <p className="muted">No events yet for this category. Trigger a sync or any platform operation to see records flow in.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Time</th>
                  {category === "all" ? <th style={{ width: 110 }}>Category</th> : null}
                  {scope === "platform" ? <th style={{ width: 180 }}>Workspace</th> : null}
                  <th style={{ width: 240 }}>Action</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const eCat = LIVE_CATEGORIES.find((c) => c.key === e.category);
                  return (
                    <tr key={e.id}>
                      <td className="soft" style={{ whiteSpace: "nowrap" }}>{fmtLiveTime(e.ts)}</td>
                      {category === "all" ? (
                        <td>
                          <span className="pill" style={{ background: eCat?.color ?? "#475569", color: "#fff" }}>
                            {eCat?.label ?? e.category}
                          </span>
                        </td>
                      ) : null}
                      {scope === "platform" ? (
                        <td className="soft" style={{ fontSize: 12 }}>{e.workspaceId ?? "—"}</td>
                      ) : null}
                      <td><code style={{ fontSize: 12 }}>{e.action}</code></td>
                      <td className="soft" style={{ fontSize: 12 }}>{summarizeLiveDetail(e.detail)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
